import { useEffect, useState } from "react";
import { monetizareAPI } from "../../api/monetizare";
import {
  freePlanDetails,
  plans,
  LANDING_LEGACY_TVA_FACTOR,
  LANDING_MIN_EUR_MONTHLY,
  retroactiveDetails,
} from "./landingContent";
import { LandingButton, LandingCard, LandingSection } from "./LandingPrimitives";

function formatRonLabel(eur: number, rate: number): string {
  return (eur * rate).toFixed(2);
}

export default function LandingPricingSection() {
  const [eurRon, setEurRon] = useState<number | null>(null);
  const [bnrDate, setBnrDate] = useState<string | null>(null);
  const [cursError, setCursError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    monetizareAPI
      .getCursEurRon()
      .then((res) => {
        if (cancelled) return;
        const r = parseFloat(res.eur_ron);
        if (!Number.isFinite(r) || r <= 0) {
          setCursError(true);
          setEurRon(null);
        } else {
          setEurRon(r);
          setBnrDate(res.data_curs);
          setCursError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCursError(true);
          setEurRon(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const titleFromCurs =
    eurRon != null
      ? `De la ${formatRonLabel(LANDING_MIN_EUR_MONTHLY, eurRon)} lei / lună — nu se aplică TVA`
      : "De la 6 EUR / lună — nu se aplică TVA";

  const description =
    eurRon != null && bnrDate
      ? `Prețul este de 2.5 EUR / vehicul / lună; nu se aplică TVA. Echivalent în lei la cursul BNR din ${bnrDate}. Abonament minim 3 vehicule; tariful se adaugă pentru fiecare vehicul peste prag. Abonamentele trimestrial și anual includ reducere.`
      : `Prețul este de 2.5 EUR / vehicul / lună; nu se aplică TVA. Echivalent în lei la cursul BNR al zilei (afișare orientativă). Abonament minim 3 vehicule; tariful se adaugă pentru fiecare vehicul peste prag. Abonamentele trimestrial și anual includ reducere.`;

  return (
    <LandingSection
      id="preturi"
      eyebrow="Prețuri"
      title={titleFromCurs}
      description={description}
    >
      {cursError ? (
        <p className="landing-pricing-curs-hint">
          Cursul BNR nu a putut fi încărcat; prețurile în EUR rămân valabile. Reîncercați mai târziu pentru echivalentul în lei.
        </p>
      ) : null}

      <div className="landing-price-grid">
        {plans.map((plan) => (
          <LandingCard
            key={plan.name}
            plain
            glow={Boolean(plan.featured)}
            className={`landing-price-card ${plan.featured ? "landing-price-card--featured" : ""} ${
              plan.name === "Lunar" ? "landing-price-card--monthly" : ""
            }`}
          >
            <div className="landing-price-card__head">
              <div>
                <h3>{plan.name}</h3>
                <p>{plan.caption}</p>
              </div>
              {plan.featured ? (
                <span className="landing-price-card__tag">Cel mai avantajos</span>
              ) : null}
            </div>

            {eurRon != null ? (
              <p className="landing-price-card__value landing-price-card__value--dual">
                <span className="landing-price-card__value-main">
                  {formatRonLabel(plan.eurMonthlyTotalMin, eurRon)} lei
                </span>
                <span className="landing-price-card__value-strike" aria-label="Preț de referință cu TVA 21%">
                  {formatRonLabel(plan.eurMonthlyTotalMin * LANDING_LEGACY_TVA_FACTOR, eurRon)} lei
                </span>
              </p>
            ) : (
              <p className="landing-price-card__value">{plan.eurMonthlyTotalMin.toFixed(2)} EUR</p>
            )}

            {eurRon != null ? (
              <p className="landing-price-card__eur-hint">
                echivalent ~{plan.eurMonthlyTotalMin.toFixed(2)} EUR / lună (min. 3 vehicule)
              </p>
            ) : null}

            <div className="landing-price-card__details">
              {plan.details.map((detail) => (
                <div key={detail} className="landing-price-pill">
                  {detail}
                </div>
              ))}
            </div>

            <div className="landing-price-note">
              Totalul de sus este calculat pentru minimum 3 vehicule.
            </div>

            <LandingButton href="/login" className="landing-button--full">
              Abonează-te
            </LandingButton>
          </LandingCard>
        ))}
      </div>

      <div className="landing-offer-grid">
        <LandingCard plain className="landing-offer-card">
          <p className="landing-offer-card__eyebrow">Plan gratuit</p>
          <h3>Testezi rapid, fără blocaje la început</h3>
          <div className="landing-price-card__details">
            {freePlanDetails.map((detail) => (
              <div key={detail} className="landing-price-pill">
                {detail}
              </div>
            ))}
          </div>
        </LandingCard>

        <LandingCard plain glow className="landing-offer-card landing-offer-card--glow">
          <p className="landing-offer-card__eyebrow landing-offer-card__eyebrow--violet">
            Completare retroactivă
          </p>
          <h3>Pentru luni sau ani în urmă</h3>
          <div className="landing-price-card__details">
            {retroactiveDetails.map((detail) => (
              <div key={detail} className="landing-price-pill">
                {detail}
              </div>
            ))}
          </div>
          <p className="landing-offer-warning">
            Fără foi de parcurs, deducerea poate fi ajustată cu 50% + penalități.
          </p>
        </LandingCard>
      </div>

      <p className="landing-pricing-footnote">Majoritatea clienților aleg anual.</p>
    </LandingSection>
  );
}
